/**
 * Clears session markers set during Auth0 callback processing.
 * MUST be called before every `logout()` call to prevent ProtectedRoute
 * from misinterpreting a stale marker as an active sign-in callback.
 *
 * The AUTH_POST_CALLBACK_TS marker is set by onRedirectCallback (in
 * Auth0ProviderWithHistory) after a SUCCESSFUL login callback. It has a
 * 5-second TTL and tells ProtectedRoute "a sign-in just completed, don't
 * redirect yet." If a user logs out within 5 seconds of logging in, this
 * stale marker can survive and cause ProtectedRoute to show "Completing
 * sign-in..." indefinitely — it waits for isAuthenticated to become true,
 * which never happens because the user just logged out.
 *
 * Note: Auth0's logout redirect does NOT trigger onRedirectCallback (the
 * SDK's hasAuthParams requires both (code||error) && state, which a clean
 * logout returnTo URL does not have). The marker is stale from the PREVIOUS
 * login, not freshly created by logout.
 *
 * The EXTERNAL_PAYMENT_REDIRECT marker is also cleared as a hygiene measure
 * to prevent stale payment-redirect re-auth attempts after logout.
 */
export function clearAuthSessionMarkers(): void {
  try {
    sessionStorage.removeItem("AUTH_POST_CALLBACK_TS");
    sessionStorage.removeItem("EXTERNAL_PAYMENT_REDIRECT");
  } catch {
    // Ignore sessionStorage errors (e.g., private browsing mode)
  }
}

/**
 * Sets the EXTERNAL_PAYMENT_REDIRECT marker then redirects to a Stripe
 * Checkout / Customer Portal URL. The marker tells ProtectedRoute that the
 * user intentionally left the SPA for a payment flow and may need Auth0
 * re-authentication on return.
 *
 * MUST be used for every `window.location.replace` / `window.location.href`
 * that sends the user to Stripe. Without the marker, ProtectedRoute's
 * 10-second timeout safety net is NOT suppressed and may fire when the user
 * returns, incorrectly redirecting them to /admin/no-hotels.
 *
 * Previously only PaymentModal.tsx set this marker. Other Stripe launchers
 * (AdminBillingPage Pro activation, UpgradeRoomsModal, usePaymentFlow)
 * redirected without it — fixed April 2026.
 */
export function redirectToStripeCheckout(checkoutUrl: string): void {
  try {
    sessionStorage.setItem("EXTERNAL_PAYMENT_REDIRECT", Date.now().toString());
  } catch {
    // Ignore sessionStorage errors (e.g., private browsing mode)
  }
  window.location.replace(checkoutUrl);
}
